import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Spin,
  message,
} from "antd";
import "./ProductBooking.css";
import {
  InfoCircleOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import Navbar from "../../../layouts/navbar";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { useProduct } from "./ProductContext";
import { useReferral } from "../../../utils/referral";
import { useCheckout } from "../../../layouts/checkout/CheckoutContext";

const { Text, Link, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

const ProductCheckout = (props: any) => {
  const { bookingId } = useParams();
  const { navigateWithReferral } = useReferral();
  const {
    currentBooking,
    createProductBooking,
    fetchTempProductBooking,
    onCurrentBooking,
    productCarts,
    removeFromCart,
  } = useProduct();
  const { user } = useAuth();
  const { checkPromotionEligibility } = useCheckout();

  // Add referral state
  const { referralCode: urlReferralCode, clearReferral } = useReferral();
  const [manualReferral, setManualReferral] = useState("");

  // Promo code states
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<any>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");

  const [filled, setFilled] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Determine the final referral code to use
  const finalReferralCode = urlReferralCode || manualReferral;

  useEffect(() => {
    if (bookingId) {
      fetchTempProductBooking(bookingId);
      console.log("check productCarts ====> ", productCarts);
    }
  }, [bookingId]);

  // Initialize form with current booking params when they become available
  useEffect(() => {
    if (currentBooking.bookingParams) {
      setName(currentBooking.bookingParams.name || "");
      setEmail(currentBooking.bookingParams.email || "");
      setPhone(currentBooking.bookingParams.phone || "");
    }
  }, [currentBooking.bookingParams]);

  // Calculate base total amount
  const calculateBaseTotal = () => {
    if (!currentBooking.bookingParams?.product_detail) return 0;

    const total = currentBooking.bookingParams.product_detail.reduce(
      (sum, product) => {
        return sum + parseFloat(product.price) * Number(product.quantity);
      },
      0
    );

    return total;
  };

  // Calculate final price with discount
  const calculateFinalPrice = () => {
    const baseTotal = calculateBaseTotal();
    let discountAmount = 0;

    // Calculate discount from promo code
    if (appliedPromo && appliedPromo.eligible === "YES") {
      discountAmount = parseFloat(appliedPromo.discount_amount || "0");
    }

    return {
      baseTotal: baseTotal,
      discountAmount: discountAmount,
      finalTotal: Math.max(0, baseTotal - discountAmount),
    };
  };

  const finalPrices = calculateFinalPrice();

  // Promo code functions
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }

    setApplyingPromo(true);
    setPromoError("");

    try {
      const result = await checkPromotionEligibility({
        ordertype: "product",
        promotion_code: promoCode.trim(),
        amount: finalPrices.baseTotal,
      });

      // Check if promo is eligible based on API response
      if (result.eligible === "YES") {
        setAppliedPromo(result);
        message.success(`Promo code applied! ${result.description}`);
      } else {
        setPromoError(result.description || "This promo code is not eligible");
      }
    } catch (err: any) {
      setPromoError(err.message || "Failed to apply promo code");
      message.error("Failed to apply promo code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoError("");
    message.info("Promo code removed");
  };

  const onAutofillForm = (checked: any) => {
    setFilled(checked);
    if (checked && user) {
      setName(`${user.firstname || ""} ${user.lastname || ""}`.trim());
      setEmail(user.email || "");
      setPhone(user.phone || "");
    } else {
      setName("");
      setEmail("");
      setPhone("");
    }
  };

  const onChangeInput = (type: string, value: any) => {
    const setters: any = {
      name: setName,
      email: setEmail,
      phone: setPhone,
    };

    if (setters[type]) {
      setters[type](value);
    }
  };

  const onPayNow = async () => {
    if (!agreed) {
      alert("Please agree to the terms and conditions");
      return;
    }

    // Validate required fields
    if (!name || !email || !phone) {
      alert("Please fill up all required fields before proceeding");
      return;
    }

    setPayingNow(true);

    const bookingParams: any = {
      ...currentBooking.bookingParams,
      name: name,
      email: email,
      phone: phone,
      promotion_code: appliedPromo?.code || "", // Add promo code
      referral: finalReferralCode || "", // Add referral code
    };

    try {
      console.log("check bookingParams ===> ", bookingParams);
      const res = await createProductBooking(bookingParams);
      if (res && res.payment_redirect_url) {
        setShowPaymentModal(true);
        // Give user time to read the message
        removeFromCart(`${currentBooking.bookingDetails?.tempId}`);
        setTimeout(() => {
          window.location.replace(`${res.payment_redirect_url}`);
        }, 2500);
      }
    } catch (error) {
      console.error("Payment error:", error);
      setShowPaymentModal(false);
    } finally {
      setPayingNow(false);
    }
  };

  // Get total quantity of all products
  const getTotalQuantity = () => {
    if (!currentBooking.bookingParams?.product_detail) return 0;

    return currentBooking.bookingParams.product_detail.reduce(
      (total, product) => {
        return total + parseInt(product.quantity);
      },
      0
    );
  };

  // Get total number of unique products (including variants as separate products)
  const getTotalProducts = () => {
    if (!currentBooking.bookingParams?.product_detail) return 0;
    return currentBooking.bookingParams.product_detail.length;
  };

  // Group products by pickup date and time
  const getGroupedPickupInfo = () => {
    if (!currentBooking.bookingDetails?.products) return [];

    const grouped: any = {};

    currentBooking.bookingDetails.products.forEach((product: any) => {
      const key = `${product.pickupDate}_${product.pickupTime}`;
      if (!grouped[key]) {
        grouped[key] = {
          date: product.pickupDate,
          time: product.pickupTime,
          products: [],
        };
      }
      grouped[key].products.push(product);
    });

    return Object.values(grouped);
  };

  const groupedPickupInfo = getGroupedPickupInfo();

  console.log("check currentBooking ===> ", currentBooking);

  // Render product list with pickup information
  const renderProductList = () => {
    if (!currentBooking.bookingDetails?.products) return null;

    return (
      <div>
        {groupedPickupInfo.map((group: any, groupIndex: number) => (
          <div key={groupIndex} style={{ marginBottom: 24 }}>
            {/* Pickup Header */}
            <div
              style={{
                padding: "12px",
                backgroundColor: "#f0f8ff",
                borderRadius: "6px",
                marginBottom: 12,
                border: "1px solid #d6e4ff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <CalendarOutlined
                  style={{ color: "#1890ff" }}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <Text strong>
                  Pickup: {moment(group.date).format("DD MMM YYYY")}
                </Text>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ClockCircleOutlined
                  style={{ color: "#52c41a" }}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <Text>Time: {group.time}</Text>
              </div>
            </div>

            {/* Products for this pickup slot */}
            <List
              dataSource={group.products}
              renderItem={(product: any) => (
                <List.Item>
                  <div style={{ width: "100%" }}>
                    {/* Product Header */}
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        marginBottom: 8,
                      }}
                    >
                      {product.product_name}
                    </div>

                    {/* Product Details */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        {/* Product ID */}
                        <div style={{ fontSize: 11, color: "#ccc" }}>
                          Product ID: {product.product_id}
                        </div>

                        {/* Selected Options */}
                        {product.variations &&
                          product.variations.length > 0 && (
                            <div
                              style={{
                                fontSize: 12,
                                color: "#999",
                                marginBottom: 4,
                              }}
                            >
                              {product.variations.map(
                                (variation: any, index: number) => (
                                  <div key={index}>
                                    <Tag
                                      color="blue"
                                      style={{
                                        fontSize: 10,
                                        margin: "2px 4px 2px 0",
                                      }}
                                    >
                                      {variation.variationTitle}:{" "}
                                      {variation.optionTitle}
                                      {variation.optionPrice > 0 &&
                                        ` (+RM ${variation.optionPrice.toFixed(
                                          2
                                        )})`}
                                    </Tag>
                                  </div>
                                )
                              )}
                            </div>
                          )}

                        {/* Price and Quantity */}
                        <div
                          style={{
                            fontSize: 14,
                            color: "#666",
                            marginBottom: 4,
                          }}
                        >
                          RM {parseFloat(`${product.pricePerItem}`).toFixed(2)}{" "}
                          × {product.quantity}
                        </div>
                      </div>

                      {/* Item Total */}
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: "500", fontSize: 16 }}>
                          RM {parseFloat(product.amount).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div id="HomeScreen">
      {/* @ts-ignore */}
      <Modal
        title="Processing Your Payment"
        visible={showPaymentModal}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <Title level={4}>Secure Payment</Title>
          <Text>You're being redirected to our secure payment gateway.</Text>
          <br />
          <br />
          <Text type="secondary">
            • Please complete your payment in the new window
            <br />
            • Do not close this page until payment is complete
            <br />• You'll be redirected back here after payment
          </Text>
          <br />
          <br />
          <div style={{ marginTop: "16px" }}>
            <Spin size="large" />
          </div>
        </div>
      </Modal>
      <div className="container">
        <Navbar />

        {/* Back Button */}
        <div style={{ marginBottom: 16 }}>
          <Button
            type="text"
            onClick={() => navigateWithReferral(-1)}
            style={{ paddingLeft: 0 }}
          >
            ← Back to Cart
          </Button>
        </div>

        <Row gutter={[24, 24]}>
          {/* Order Summary - Left Side */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text strong style={{ fontSize: 18 }}>
                    Order Summary
                  </Text>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Tag color="blue">{getTotalQuantity()} items</Tag>
                    <Tag color="green">
                      {groupedPickupInfo.length} pickup slot(s)
                    </Tag>
                  </div>
                </div>
              }
              style={{ marginBottom: 24 }}
            >
              {renderProductList()}

              <Divider />

              {/* Order Total */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <Text>Subtotal:</Text>
                <Text>RM {finalPrices.baseTotal.toFixed(2)}</Text>
              </div>

              {appliedPromo && appliedPromo.eligible === "YES" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                    color: "#52c41a",
                  }}
                >
                  <Text>Discount ({appliedPromo.code}):</Text>
                  <Text>- RM {finalPrices.discountAmount.toFixed(2)}</Text>
                </div>
              )}

              <Divider style={{ margin: "12px 0" }} />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong style={{ fontSize: 18 }}>
                  Total Amount
                </Text>
                <Text strong style={{ fontSize: 20, color: "#e90056" }}>
                  RM {finalPrices.finalTotal.toFixed(2)}
                </Text>
              </div>
            </Card>
          </Col>

          {/* Checkout Form - Right Side */}
          <Col xs={24} lg={12}>
            <Card title="Customer Details" style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={filled}
                  onChange={(e) => {
                    onAutofillForm(e.target.checked);
                  }}
                >
                  Fill with my profile's info
                </Checkbox>
              </div>

              <Form layout="vertical">
                <Form.Item
                  label="Full Name"
                  required
                  style={{ marginBottom: 16 }}
                >
                  <Input
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => onChangeInput("name", e.target.value)}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Email Address"
                  required
                  style={{ marginBottom: 16 }}
                >
                  <Input
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => onChangeInput("email", e.target.value)}
                    size="large"
                    type="email"
                  />
                </Form.Item>

                <Form.Item
                  label="Phone Number"
                  required
                  style={{ marginBottom: 16 }}
                >
                  <Input
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => onChangeInput("phone", e.target.value)}
                    size="large"
                  />
                </Form.Item>

                {/* Referral Code Section */}
                <Form.Item
                  label="Referral Code (optional)"
                  style={{ marginBottom: 16 }}
                >
                  {urlReferralCode ? (
                    <div>
                      <Input
                        value={urlReferralCode}
                        disabled
                        size="large"
                        placeholder="Referral code from URL"
                      />
                      <Text
                        type="secondary"
                        style={{
                          fontSize: "12px",
                          marginTop: "4px",
                          display: "block",
                        }}
                      >
                        Referral code applied from your invitation link
                      </Text>
                    </div>
                  ) : (
                    <Input
                      value={manualReferral}
                      onChange={(e) => setManualReferral(e.target.value)}
                      size="large"
                      placeholder="Enter referral code (optional)"
                    />
                  )}
                </Form.Item>

                {/* Promo Code Section */}
                <Form.Item
                  label="Promo Code (optional)"
                  style={{ marginBottom: 16 }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "flex-start",
                    }}
                  >
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="Enter promo code"
                      disabled={applyingPromo || !!appliedPromo}
                      size="large"
                      style={{ flex: 1 }}
                    />
                    {appliedPromo ? (
                      <Button
                        onClick={handleRemovePromo}
                        danger
                        type="default"
                        size="large"
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        onClick={handleApplyPromo}
                        loading={applyingPromo}
                        type="primary"
                        disabled={!promoCode.trim()}
                        size="large"
                      >
                        Apply
                      </Button>
                    )}
                  </div>
                  {promoError && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {promoError}
                    </Text>
                  )}
                  {appliedPromo && appliedPromo.eligible === "YES" && (
                    <div style={{ marginTop: "4px" }}>
                      <Text
                        type="success"
                        style={{ fontSize: "12px", display: "block" }}
                      >
                        ✅ {appliedPromo.description}
                      </Text>
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px", display: "block" }}
                      >
                        Valid until:{" "}
                        {moment(appliedPromo.expirydate).format("DD MMM YYYY")}
                      </Text>
                    </div>
                  )}
                </Form.Item>
              </Form>
            </Card>

            {/* Terms and Conditions */}
            <Card title="Terms & Conditions">
              <div style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 14, lineHeight: 1.5, color: "#666" }}>
                  By proceeding with this booking, you agree to our terms and
                  conditions. Please ensure all information provided is
                  accurate. You will receive a confirmation email upon
                  successful booking.
                </Text>
              </div>

              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                style={{ marginBottom: 16 }}
              >
                I agree to the terms and conditions
              </Checkbox>

              <Button
                type="primary"
                size="large"
                block
                loading={payingNow}
                onClick={onPayNow}
                disabled={!agreed || !name || !email || !phone}
                style={{
                  height: 50,
                  fontSize: 16,
                  backgroundColor: "#e90056",
                  borderColor: "#e90056",
                }}
              >
                {payingNow ? "Processing Booking..." : "Confirm Booking"}
              </Button>

              {(!name || !email || !phone) && (
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    marginTop: 8,
                    textAlign: "center",
                  }}
                >
                  Please fill all required fields
                </Text>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ProductCheckout;
